import { format } from 'date-fns';
import Link from 'next/link';
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  type UseTrackDepletedStateViewedParams,
  useTrackDepletedStateViewed,
  useTrackDepletedUpsellClicked,
} from '@air/analytics';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { useAirModal } from '@air/provider-modal';
import { parseAirDateToISO } from '@air/utilities';

import { EnterprisePlanModal } from '~/components/Modals/EnterprisePlanModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { Routes } from '~/constants/routes';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export type CreditsDepletedModalTrigger = 'canvas' | 'assetEdit';
export type CreditsDepletedModalReason = 'creditsDepleted' | 'paidPlanRequired';

export type CreditsDepletedModalProps = {
  reason?: CreditsDepletedModalReason;
  triggeredBy?: CreditsDepletedModalTrigger;
};

const SURFACE_BY_TRIGGER: Record<CreditsDepletedModalTrigger, UseTrackDepletedStateViewedParams['surface']> = {
  canvas: 'canvas',
  assetEdit: 'asset_edit',
};

/** Without a trigger the modal is being shown for a blocked upload, which is where it started out. */
const DEFAULT_SURFACE: UseTrackDepletedStateViewedParams['surface'] = 'upload_modal';

const DIALOG_TITLE_ID = 'credits-depleted-modal-title';
const DIALOG_DESCRIPTION_ID = 'credits-depleted-modal-description';

export const CreditsDepletedModal = memo((props: AirModalProps<CreditsDepletedModalProps>) => {
  const { reason = 'creditsDepleted', triggeredBy, onClose } = props;
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: creditStats } = useCreditStats();
  const { data: subscription } = useSubscriptionContext();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canEditPlan = canManageSubscription(permissions);

  const [showPlansModal] = useAirModal(PlansModal);
  const [showEnterprisePlanModal] = useAirModal(EnterprisePlanModal);

  const { trackDepletedStateViewed } = useTrackDepletedStateViewed();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedView = useRef<boolean>();

  const surface = triggeredBy ? SURFACE_BY_TRIGGER[triggeredBy] : DEFAULT_SURFACE;

  // useEffect re-runs when creditAnalyticsParams loads; guard prevents duplicate events
  useEffect(() => {
    if (reason === 'creditsDepleted' && creditAnalyticsParams && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackDepletedStateViewed({
        surface,
        ...creditAnalyticsParams,
      });
    }
  }, [creditAnalyticsParams, reason, surface, trackDepletedStateViewed]);

  const handleClick = useCallback(() => {
    // Only track upsell click when user can actually upgrade (not viewing member list)
    if (reason === 'creditsDepleted' && canEditPlan && creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface,
        ...creditAnalyticsParams,
      });
    }

    if (canEditPlan) {
      if (subscription?.isOnEnterprisePlan) {
        showEnterprisePlanModal();
      } else {
        showPlansModal();
      }
    }

    onClose();
  }, [
    canEditPlan,
    creditAnalyticsParams,
    onClose,
    reason,
    showEnterprisePlanModal,
    showPlansModal,
    subscription?.isOnEnterprisePlan,
    surface,
    trackDepletedUpsellClicked,
  ]);

  const { title, description } = useMemo(() => {
    let title = 'You’re out of credits';
    let actionText = 'Uploads are';
    const refreshDateText = creditStats?.nextCycleStartAt
      ? `refresh on ${format(parseAirDateToISO(creditStats.nextCycleStartAt), 'MMMM d')}`
      : 'refresh';

    if (triggeredBy === 'canvas') {
      title = 'Your Canvas is locked';
      actionText = 'Canvas editing is';
    } else if (triggeredBy === 'assetEdit') {
      actionText = 'AI editing is';
    }

    let description: ReactNode;

    if (reason === 'paidPlanRequired') {
      title = 'Upgrade to use AI tools';
      description =
        'AI and generative actions require a paid plan. Upgrade your plan to use these features. Your workspace, search, and shared links are still fully accessible.';
    } else {
      description = `${actionText} paused until your credits ${refreshDateText} or you upgrade your plan. Your workspace, search, and shared links are still fully accessible.`;
    }

    if (!canEditPlan && currentWorkspace) {
      description = (
        <>
          {description}{' '}
          <span className="font-semibold">Contact the admin for {currentWorkspace.name} to upgrade your plan.</span>
        </>
      );
    }

    return {
      title,
      description,
    };
  }, [canEditPlan, creditStats?.nextCycleStartAt, currentWorkspace, reason, triggeredBy]);

  return (
    <Modal
      aria-describedby={DIALOG_DESCRIPTION_ID}
      aria-labelledby={DIALOG_TITLE_ID}
      data-testid="CREDITS_DEPLETED_MODAL"
      isOpen
      onDismiss={onClose}
      className="mt-0 flex w-[354px] flex-col gap-4"
      overlayClassName="flex items-center justify-center"
    >
      <header className="flex justify-between">
        <ModalTitle data-testid="CREDITS_DEPLETED_MODAL_TITLE" id={DIALOG_TITLE_ID}>
          {title}
        </ModalTitle>
        <ModalCloseButton className="shrink-0" onClick={onClose} />
      </header>
      <ModalDescription
        className="ads:text-secondary text-14 text-gray-11"
        data-testid="CREDITS_DEPLETED_MODAL_DESCRIPTION"
        id={DIALOG_DESCRIPTION_ID}
      >
        {description}
      </ModalDescription>
      <div className="flex justify-end gap-2">
        {canEditPlan ? (
          <Button
            type="button"
            onClick={handleClick}
            size="large"
            appearance="filled"
            color="blue"
            className="w-full"
            data-testid="CREDITS_DEPLETED_MODAL_ACTION"
          >
            {subscription?.isOnEnterprisePlan ? 'Contact support' : 'Upgrade plan'}
          </Button>
        ) : (
          <>
            <Button type="button" size="large" appearance="outline" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              size="large"
              appearance="filled"
              color="blue"
              asChild
              data-testid="CREDITS_DEPLETED_MODAL_ACTION"
            >
              <Link href={Routes.workspaceSettings.members} onClick={handleClick}>
                View workspace user list
              </Link>
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
});

CreditsDepletedModal.displayName = 'CreditsDepletedModal';
